import { useQuery, useQueryClient } from 'react-query';
import { useEffect, useMemo } from 'react';
import { PagedResult } from '@dirootie-core/core-types';
import { ApiServiceGeneric } from '@dirootie-http/api-types';
import { IKeyedObject } from '@dirootie-extensions/typescript';
import { useContainer, useHttpClient } from '../../hooks';
import { IServiceProvider } from '../../configuration/modules/modules.types';
import {
  MemorySource,
  RemoteSource,
  DataSource,
  QueryState,
} from '@components-common/types';
import { cond, has, prop, propEq } from 'ramda';
import { isArray } from 'ramda-adjunct';
import {
  isMemorySource,
  isRemoteSource,
  mapQuery,
} from '../../components-common/tables/dirootie';
import { IRoutingService } from '../../components-common/routes';
import { useApiUrl, useApiUrlBuilder } from './use-api.hook';
import { useParams } from 'react-router-dom';

const getMemorySource = (
  query: any,
  elements: any[],
  params?: IKeyedObject<string>
) => {
  const result = {
    page: query?.page || 1,
    size: query?.size || 10,
    items: elements,
    total: elements.length,
  };
  return Promise.resolve(result);
};

const extractValue = (str: any, queryParams: any) => {
  const regex = /\{(.*?)\}/;
  const match = str.match(regex);
  return match ? queryParams[match[1]] : str;
};

export const useApiGetAll = () => {
  const build = useApiUrlBuilder();
  const httpClient = useHttpClient();
  const queryParams = useParams();
  return useMemo(
    () => async (query: QueryState, source: RemoteSource) => {
      let processedData = {};
      if (source.urlParams) {
        processedData = Object.fromEntries(
          Object.entries(source.urlParams as any).map(([key, value]) => [
            key,
            extractValue(value, queryParams),
          ])
        );
      }
      const url = build(source.url, {
        ...(mapQuery(query) as any),
        ...processedData,
      });
      const res = await httpClient.get<PagedResult<any>>(url);
      return res.body;
    },
    [build, httpClient]
  );
};

export const useGetAll = (query: QueryState, source: DataSource) => {
  const queryClient = useQueryClient();
  const getAll = useApiGetAll();
  const { key } = source;
  const { data, isLoading, refetch } = useQuery(
    [key, query],
    () =>
      getAll(query, source as RemoteSource),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
    }
  );

  return { data, isLoading, refetch };
};
